import { useBranches } from "@api";
import { GridSectionSpan, SimpleTable, Tab, TabGroup } from "@components";
import { cn, formatAmount, formatDateTime } from "@utils";
import { useEffect, useMemo, useState } from "react";
import { useMerchant } from "@auth";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

const PayoutTransactions = ({ transactions, onTransactionClick }) => {
  const { merchant } = useMerchant();
  const [viewMode, setViewMode] = useState("all");
  const [expandedBranches, setExpandedBranches] = useState({});
  const { data: branches = [] } = useBranches();

  const defaultBranchName = useMemo(() => {
    const defaultBranch = branches.find((b) => b.isDefault) ?? branches[0];
    return defaultBranch?.name ?? "Unknown";
  }, [branches]);

  const branchLookup = useMemo(() => {
    const lookup = {};
    for (const b of branches) {
      lookup[b.id] = b.name;
    }
    return lookup;
  }, [branches]);

  const tableConfig = () => {
    return {
      onClick: onTransactionClick,
      columns: [
        {
          title: "Transaction date",
          key: "transactionDate",
          width: "120px",
          render: (x) => formatDateTime(x.transactionDate),
        },
        {
          title: "Type",
          width: "120px",
          render: (x) => {
            switch (x.type) {
              case "CardPayment":
              case "ThirdPartyPayment":
                return "Payment";
              case "CardRefund":
              case "ThirdPartyRefund":
                return "Refund";
              default:
                return "unknown";
            }
          },
          key: "type",
        },
        {
          title: "Reference",
          width: "120px",
          render: (x) => x.reference,
          key: "reference",
        },
        {
          title: "Customer",
          width: "120px",
          render: (x) => x.customerName,
          key: "customerName",
        },
        {
          title: "Customer branch",
          width: "120px",
          render: (x) => x.customerBranch,
          key: "customerBranch",
          shouldShow: () => merchant?.erpSettings?.showBranch,
        },
        {
          title: "Amount",
          alignment: "right",
          width: "120px",
          render: (x) =>
            formatAmount(
              ["CardRefund", "ThirdPartyRefund"].includes(x.type)
                ? -Math.abs(x.amount)
                : x.amount,
            ),
          key: "amount",
        },
      ],
    };
  };

  const branchGroups = useMemo(() => {
    if (!transactions) return [];
    const groups = {};
    for (const t of transactions) {
      const key = t.branchId ?? "unassigned";
      if (!groups[key]) {
        groups[key] = {
          branchId: key,
          name:
            key === "unassigned"
              ? defaultBranchName
              : (branchLookup[key] ?? "Unknown"),
          transactions: [],
          total: 0,
        };
      }
      groups[key].transactions.push(t);
      groups[key].total += t.amount;
    }
    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  }, [transactions, branchLookup, defaultBranchName]);

  const toggleBranch = (branchId) => {
    setExpandedBranches((prev) => ({ ...prev, [branchId]: !prev[branchId] }));
  };

  return (
    <>
      <GridSectionSpan>
        <div className="flex items-center justify-between w-full mb-6">
          <h2 className="text-lg font-semibold m-0">
            {transactions.length > 0
              ? "Transactions"
              : "Awaiting transaction breakdown"}
          </h2>
          {transactions.length > 0 && (
            <TabGroup className="my-0 w-48">
              <Tab
                active={viewMode === "all"}
                onClick={() => setViewMode("all")}
              >
                All
              </Tab>
              <Tab
                active={viewMode === "branch"}
                onClick={() => setViewMode("branch")}
              >
                Branch
              </Tab>
            </TabGroup>
          )}
        </div>
      </GridSectionSpan>
      {viewMode === "all" ? (
        <GridSectionSpan>
          <div className="w-full">
            <SimpleTable
              config={tableConfig()}
              data={transactions}
              noPad={true}
              enableExport={true}
            />
          </div>
        </GridSectionSpan>
      ) : (
        branchGroups.map((group, idx) => (
          <GridSectionSpan key={group.branchId}>
            <div className={cn("w-full py-4", idx > 0 && "border-t border-gray-200")}>
              <div className="flex items-start justify-between">
                <h3 className="text-sm font-medium m-0">{group.name}</h3>
                <div className="text-right">
                  <div className="text-base font-medium">
                    {formatAmount(group.total)}
                  </div>
                  <button
                    className="text-xs font-semibold text-orange-500 cursor-pointer bg-transparent border-0 p-0"
                    onClick={() => toggleBranch(group.branchId)}
                  >
                    See transactions ({group.transactions.length}){" "}
                    <ChevronDownIcon
                      className={cn("inline size-4", expandedBranches[group.branchId] && "rotate-180")}
                    />
                  </button>
                </div>
              </div>
              {expandedBranches[group.branchId] && (
                <div className="w-full mt-3">
                  <SimpleTable
                    config={tableConfig()}
                    data={group.transactions}
                    noPad={true}
                    enableExport={true}
                  />
                </div>
              )}
            </div>
          </GridSectionSpan>
        ))
      )}
    </>
  );
};

export default PayoutTransactions;
